import { useContext, useRef, useState } from 'react';
import { useUserStore } from '@/user/stores/userStore';
import { useTranslation } from 'next-i18next';
import {
  Box,
  Link,
  List,
  ListItemText,
  Popover,
  useTheme
} from '@mui/material';
import { CreateWorkspaceModal } from './components/CreateWorkspaceModal/CreateWorkspaceModal';
import { WorkspacesMenuButton } from './components/WorkspacesMenuButton/WorkspacesMenuButton';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { sortById } from '@/common/utils/sortById';
import { MenuToggleContext } from '@/base/layouts/contexts/MenuToggleContext';
import { WorkspaceListItem } from '@/common/components/WorkspaceListItem/WorkspaceListItem';

export const WorkspacesMenu = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const ref = useRef<HTMLSpanElement>(null);
  const { workspaces, setCurrentWorkspace, setWorkspaces, currentWorkspace } =
    useUserStore();
  const { closeMenu } = useContext(MenuToggleContext);

  const workspace = workspaces.find((w) => w.id === currentWorkspace);

  const [isWorkspacesMenuOpen, setWorkspacesMenuOpen] =
    useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    setWorkspacesMenuOpen(true);
  };

  const handleClose = (): void => {
    setWorkspacesMenuOpen(false);
    closeMenu();
  };

  const handleChangeWorkspace = (workspaceId: string): void => {
    const sortByCurrentId = sortById(workspaceId);
    const sortedWorkspaces = workspaces.sort(sortByCurrentId);

    setWorkspaces(sortedWorkspaces);
    window.localStorage.setItem('currentWorkspace', workspaceId);
    setCurrentWorkspace(workspaceId);
    setWorkspacesMenuOpen(false);
  };

  return (
    <>
      <Box component={'span'} ref={ref}>
        <WorkspaceListItem
          workspace={workspace}
          onClick={handleOpen}
          showUnfold
          styles={{
            cursor: 'pointer'
          }}
        />
      </Box>
      <Popover
        disableScrollLock
        anchorEl={ref.current}
        onClose={handleClose}
        open={isWorkspacesMenuOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box
          component="div"
          display="flex"
          width={312}
          sx={{
            pt: 2,
            pl: 2,
            pr: 2
          }}
        >
          <ListItemText primary={t('Your workspaces')} />
          <ListItemText
            primary={workspaces.length}
            sx={{
              textAlign: 'right'
            }}
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          p={theme.spacing(3, 2)}
          gap={1}
        >
          {workspaces.map((workspace) => (
            <WorkspaceListItem
              key={workspace.id}
              workspace={workspace}
              onClick={() => handleChangeWorkspace(workspace.id)}
              showCheck
              styles={{
                cursor: 'pointer'
              }}
            />
          ))}
        </Box>

        <List
          sx={{
            borderTop: `1px solid ${theme.sidebar.dividerBg}`
          }}
        >
          <Link
            onClick={() => {
              setIsCreateModalOpen(true);
              setWorkspacesMenuOpen(false);
            }}
            style={{
              color: theme.colors.primary.main,
              cursor: 'pointer'
            }}
            underline="none"
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              color={theme.colors.primary.main}
              p={theme.spacing(1.35, 1.7)}
            >
              <AddIcon />
              <ListItemText
                primary={t('Create workspace')}
                primaryTypographyProps={{
                  fontWeight: theme.typography.fontWeightBold
                }}
              />
            </Box>
          </Link>

          <WorkspacesMenuButton
            link="/workspaces"
            name={t('Settings')}
            icon={SettingsIcon}
            handleCloseMenu={handleClose}
            styles={{ pl: 0.6 }}
          />
        </List>
      </Popover>

      <CreateWorkspaceModal
        isModalOpen={isCreateModalOpen}
        setIsModalOpen={setIsCreateModalOpen}
      />
    </>
  );
};
